/**
 * Analytics Service
 * 
 * Google Analytics integration and analytics data management
 */

import { ServiceResponse, successResponse, errorResponse, handleServiceError, notConfiguredError } from '@/lib/service-errors';

export interface AnalyticsConfig {
  googleAnalyticsId?: string;
  googleTagManagerId?: string;
  enabled: boolean;
}

export interface AnalyticsMetrics {
  pageViews: number;
  uniqueVisitors: number;
  bounceRate: number;
  avgSessionDuration: number;
  topPages: Array<{ path: string; views: number }>;
  topReferrers: Array<{ source: string; visits: number }>;
  deviceBreakdown: {
    desktop: number;
    mobile: number;
    tablet: number;
  };
  browserBreakdown: Array<{ browser: string; percentage: number }>;
}

/**
 * Get analytics configuration
 */
export const getAnalyticsConfig = async (): Promise<ServiceResponse<AnalyticsConfig>> => {
  try {
    const config: AnalyticsConfig = {
      googleAnalyticsId: import.meta.env.VITE_GA_MEASUREMENT_ID || import.meta.env.VITE_GOOGLE_ANALYTICS_ID,
      googleTagManagerId: import.meta.env.VITE_GTM_ID,
      enabled: !!(import.meta.env.VITE_GA_MEASUREMENT_ID || import.meta.env.VITE_GOOGLE_ANALYTICS_ID),
    };

    return successResponse(config);
  } catch (error) {
    return errorResponse(handleServiceError(error, 'analyticsService.getAnalyticsConfig'));
  }
};

/**
 * Check if Google Analytics is configured
 */
export const isAnalyticsConfigured = (): boolean => {
  return !!(import.meta.env.VITE_GA_MEASUREMENT_ID || import.meta.env.VITE_GOOGLE_ANALYTICS_ID);
};

/**
 * Track page view
 */
export const trackPageView = (path: string, title?: string): void => {
  if (!isAnalyticsConfigured()) {
    return;
  }

  // Google Analytics 4 (gtag)
  if (window.gtag) {
    window.gtag('config', import.meta.env.VITE_GA_MEASUREMENT_ID || import.meta.env.VITE_GOOGLE_ANALYTICS_ID, {
      page_path: path,
      page_title: title,
    });
  }

  // Google Analytics Universal (ga)
  if (window.ga) {
    window.ga('send', 'pageview', path);
  }
};

/**
 * Track custom event
 */
export const trackEvent = (eventName: string, eventParams?: Record<string, any>): void => {
  if (!isAnalyticsConfigured()) {
    return;
  }

  // Google Analytics 4 (gtag)
  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
  }

  // Google Analytics Universal (ga)
  if (window.ga) {
    window.ga('send', 'event', eventName, eventParams?.action || 'click', eventParams?.label || '');
  }
};

/**
 * Get analytics metrics from Google Analytics 4 Data API
 * 
 * This function attempts to fetch real data from a backend API endpoint.
 * If the backend API is not available, it falls back to mock data.
 * 
 * To enable real analytics data:
 * 1. Set up a backend API route (e.g., /api/analytics) that authenticates with GA4 Data API
 * 2. The backend should use a service account with Analytics Reporting API access
 * 3. Set VITE_ANALYTICS_API_URL environment variable to point to your backend endpoint
 */
export const getAnalyticsMetrics = async (
  startDate?: string,
  endDate?: string
): Promise<ServiceResponse<AnalyticsMetrics>> => {
  if (!isAnalyticsConfigured()) {
    return errorResponse(notConfiguredError('Google Analytics'));
  }

  try {
    // Calculate date range if not provided
    const end = endDate || new Date().toISOString().split('T')[0];
    const start = startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // Default to last 30 days

    // Try to fetch from backend API if available
    const apiUrl = import.meta.env.VITE_ANALYTICS_API_URL;
    if (apiUrl) {
      try {
        const response = await fetch(`${apiUrl}?startDate=${start}&endDate=${end}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            return successResponse(data.data);
          }
        }
      } catch (apiError) {
        console.warn('[analyticsService] Backend API not available, using fallback:', apiError);
        // Fall through to mock data
      }
    }

    // Fallback: Return empty structure (will be populated by real API when available)
    // Note: In a production setup, you would integrate with Google Analytics Data API here
    // using a service account or OAuth2 authentication
    const metrics: AnalyticsMetrics = {
      pageViews: 0,
      uniqueVisitors: 0,
      bounceRate: 0,
      avgSessionDuration: 0,
      topPages: [],
      topReferrers: [],
      deviceBreakdown: {
        desktop: 0,
        mobile: 0,
        tablet: 0,
      },
      browserBreakdown: [],
    };

    return successResponse(metrics);
  } catch (error) {
    return errorResponse(handleServiceError(error, 'analyticsService.getAnalyticsMetrics', { startDate, endDate }));
  }
};

// Extend Window interface for gtag and ga
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    ga?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

export const analyticsService = {
  getAnalyticsConfig,
  isAnalyticsConfigured,
  trackPageView,
  trackEvent,
  getAnalyticsMetrics,
};

